// 客户端Shadowing练习记录追踪系统
import { calculateTextDiff, DiffType } from "./text-diff-highlight-improved";

export interface ShadowingRecord {
  id: string;
  userId?: string;
  episodeId: string;
  episodeTitle: string;
  episodeThumbnail?: string;
  sentenceText: string;
  userTranscription: string;
  accuracy: number;
  timestamp: Date;
  duration: number; // 秒
  attemptNumber: number;
  mistakes: {
    missing: string[];
    wrong: string[];
    extra: string[];
    // 新增：带位置信息的详细错误数据（向后兼容）
    detailedMistakes?: {
      missing: Array<{ word: string; originalPosition: number }>;
      wrong: Array<{ originalWord: string; userWord: string; originalPosition: number; userPosition: number }>;
      extra: Array<{ word: string; userPosition: number }>;
    };
  };
}

export interface ShadowingStats {
  totalSessions: number;
  totalTime: number; // 总练习时间（秒）
  averageAccuracy: number;
  bestAccuracy: number;
  streak: number; // 连续练习天数
  lastPracticeDate: Date | null;
  progressTrend: number[]; // 最近10次的准确度
}

export interface DailyGoal {
  targetSessions: number;
  targetMinutes: number;
  completedSessions: number;
  completedMinutes: number;
  date: string; // YYYY-MM-DD
  completionPercentage: number;
  formattedTime: string;
}

export interface DashboardStats {
  goal: {
    targetHours: number;
    completedHours: number;
    percentage: string;
    formatted: string;
  };
  today: {
    time: string;
    sessions: number;
    label: string;
  };
  week: {
    time: string;
    sessions: number;
    label: string;
  };
  month: {
    time: string;
    sessions: number;
    label: string;
  };
  streak: number;
}

class ShadowingTracker {
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private inFlight: Map<string, Promise<any>> = new Map();
  private readonly CACHE_DURATION = 60000; // 1分钟缓存

  // 缓存辅助方法
  private getCached<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    const now = Date.now();
    const cached = this.cache.get(key);

    // 命中有效缓存（返回已解析的数据，避免再次请求）
    if (cached && (now - cached.timestamp) < this.CACHE_DURATION) {
      return Promise.resolve(cached.data as T);
    }

    // 有并发中的请求则复用同一个 Promise
    const existing = this.inFlight.get(key) as Promise<T> | undefined;
    if (existing) return existing;

    // 开始新的请求，并登记为 in-flight
    const promise = fetcher()
      .then((data) => {
        // 成功后写入缓存
        this.cache.set(key, { data, timestamp: Date.now() });
        return data;
      })
      .catch((error) => {
        // 保证错误不会污染 inFlight 状态
        console.error('Error fetching data for cache:', { key, error });
        throw error;
      })
      .finally(() => {
        // 不论成功失败，都移除 inFlight 记录
        this.inFlight.delete(key);
      });

    this.inFlight.set(key, promise as Promise<any>);
    return promise;
  }

  // 清除缓存
  private clearCache(): void {
    this.cache.clear();
  }

  // 分析用户录音与原文的差异
  private analyzeMistakes(originalText: string, userTranscription: string): {
    missing: string[];
    wrong: string[];
    extra: string[];
    detailedMistakes?: {
      missing: Array<{ word: string; originalPosition: number }>;
      wrong: Array<{ originalWord: string; userWord: string; originalPosition: number; userPosition: number }>;
      extra: Array<{ word: string; userPosition: number }>;
    };
  } {
    const diffSegments = calculateTextDiff(originalText, userTranscription);

    const missing: string[] = [];
    const wrong: string[] = [];
    const extra: string[] = [];

    // 详细错误信息（带位置）
    const detailedMissing: Array<{ word: string; originalPosition: number }> = [];
    const detailedWrong: Array<{ originalWord: string; userWord: string; originalPosition: number; userPosition: number }> = [];
    const detailedExtra: Array<{ word: string; userPosition: number }> = [];

    let originalPosition = 0;
    let userPosition = 0;

    diffSegments.forEach(segment => {
      switch (segment.type) {
        case DiffType.DELETE:
          missing.push(segment.text);
          detailedMissing.push({
            word: segment.text,
            originalPosition
          });
          originalPosition++;
          break;
        case DiffType.INSERT:
          extra.push(segment.text);
          detailedExtra.push({
            word: segment.text,
            userPosition
          });
          userPosition++;
          break;
        case DiffType.SUBSTITUTE:
          if (segment.originalText) {
            wrong.push(`${segment.originalText} → ${segment.text}`);
            detailedWrong.push({
              originalWord: segment.originalText,
              userWord: segment.text,
              originalPosition,
              userPosition
            });
          }
          originalPosition++;
          userPosition++;
          break;
        case DiffType.EQUAL:
          originalPosition++;
          userPosition++;
          break;
      }
    });

    return {
      missing,
      wrong,
      extra,
      detailedMistakes: {
        missing: detailedMissing,
        wrong: detailedWrong,
        extra: detailedExtra
      }
    };
  }

  // 保存练习记录
  async saveRecord(data: {
    userId?: string;
    episodeId: string;
    episodeTitle: string;
    episodeThumbnail?: string;
    sentenceText: string;
    userTranscription: string;
    accuracy: number;
    duration: number;
    quizSequence?: number;
  }): Promise<void> {
    try {
      // 分析错误
      const mistakes = this.analyzeMistakes(data.sentenceText, data.userTranscription);

      // 简化：暂时不获取尝试次数，避免额外的API调用
      // TODO: 可以考虑在后端计算attemptNumber，或使用本地缓存
      const attemptNumber = 1;

      const record = {
        ...data,
        attemptNumber,
        mistakes
      };

      // 发送到API
      const response = await fetch('/api/shadowing-stats', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: data.userId || 'anonymous',
          record
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save record');
      }

      // 清除缓存，强制下次获取最新数据
      this.clearCache();

      // 使用防抖延迟触发事件，避免立即触发多个刷新
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('shadowingRecordSaved', {
          detail: {
            record,
            response: response.ok,
            episodeId: data.episodeId, // 添加 episodeId 到事件详情
            accuracy: data.accuracy
          }
        }));
      }, 500); // 减少延迟到500ms，更快响应

    } catch (error) {
      console.error('Error saving shadowing record:', error);
      // 可以考虑本地备份存储
      this.saveToLocalStorage(data);
      throw error;
    }
  }

  // 本地存储备份
  private saveToLocalStorage(data: any): void {
    try {
      const key = 'shadowing_backup';
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      existing.push({ ...data, timestamp: new Date().toISOString() });

      // 只保留最近50条
      if (existing.length > 50) {
        existing.splice(0, existing.length - 50);
      }

      localStorage.setItem(key, JSON.stringify(existing));
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
    }
  }

  // 获取尝试次数
  private async getAttemptNumber(
    episodeId: string,
    sentenceText: string,
    userId?: string
  ): Promise<number> {
    try {
      const params = new URLSearchParams({
        userId: userId || 'anonymous',
        type: 'records',
        limit: '100'
      });

      const response = await fetch(`/api/shadowing-stats?${params}`);
      if (!response.ok) return 1;

      const records: ShadowingRecord[] = await response.json();
      const sameRecords = records.filter(r =>
        r.episodeId === episodeId && r.sentenceText === sentenceText
      );

      return sameRecords.length + 1;
    } catch {
      return 1;
    }
  }

  // 获取仪表板统计数据
  async getDashboardStats(userId?: string): Promise<DashboardStats> {
    const tzOffset = new Date().getTimezoneOffset();
    const cacheKey = `dashboard_${userId || 'anonymous'}_${tzOffset}`;

    return this.getCached(cacheKey, async () => {
      const tzOffset = new Date().getTimezoneOffset();
      const params = new URLSearchParams({
        userId: userId || 'anonymous',
        type: 'dashboard',
        tzOffset: String(tzOffset)
      });

      const response = await fetch(`/api/shadowing-stats?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch dashboard stats');
      }

      return response.json();
    });
  }

  // 获取练习记录
  async getRecords(userId?: string, limit: number = 50, offset: number = 0): Promise<ShadowingRecord[]> {
    const cacheKey = `records_${userId || 'anonymous'}_${limit}_${offset}`;

    return this.getCached(cacheKey, async () => {
      const params = new URLSearchParams({
        userId: userId || 'anonymous',
        type: 'records',
        limit: limit.toString(),
        offset: offset.toString()
      });

      const response = await fetch(`/api/shadowing-stats?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch records');
      }

      const records = await response.json();
      // 转换日期字符串为Date对象
      return records.map((r: any) => ({
        ...r,
        timestamp: new Date(r.timestamp)
      }));
    });
  }

  // 获取进度报告
  async getProgressReport(userId?: string, days: number = 7): Promise<{
    dailyData: { date: string; sessions: number; accuracy: number }[];
    improvement: number;
    mostPracticedSentences: { text: string; count: number }[];
  }> {
    const tzOffset = new Date().getTimezoneOffset();
    const cacheKey = `progress_${userId || 'anonymous'}_${days}_${tzOffset}`;

    return this.getCached(cacheKey, async () => {
      const tzOffset = new Date().getTimezoneOffset();
      const params = new URLSearchParams({
        userId: userId || 'anonymous',
        type: 'progress',
        days: days.toString(),
        tzOffset: String(tzOffset)
      });

      const response = await fetch(`/api/shadowing-stats?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch progress report');
      }

      return response.json();
    });
  }

  // 获取月度统计数据
  async getMonthlyStats(userId?: string, months: number = 6): Promise<{
    month: string;
    value: number;
    sessions: number;
    change: number;
  }[]> {
    const tzOffset = new Date().getTimezoneOffset();
    const cacheKey = `monthly_${userId || 'anonymous'}_${months}_${tzOffset}`;

    return this.getCached(cacheKey, async () => {
      const tzOffset = new Date().getTimezoneOffset();
      const params = new URLSearchParams({
        userId: userId || 'anonymous',
        type: 'monthly',
        months: months.toString(),
        tzOffset: String(tzOffset)
      });

      const response = await fetch(`/api/shadowing-stats?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch monthly stats');
      }

      return response.json();
    });
  }

  // 设置每日目标
  async setDailyGoal(targetSessions: number, targetMinutes: number, userId?: string): Promise<void> {
    try {
      const response = await fetch('/api/shadowing-stats', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userId || 'anonymous',
          targetSessions,
          targetMinutes
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to set daily goal');
      }

      // 清除相关缓存
      this.clearCache();

      // 触发目标更新事件
      window.dispatchEvent(new CustomEvent('shadowingGoalUpdated', {
        detail: { targetSessions, targetMinutes }
      }));

    } catch (error) {
      console.error('Error setting daily goal:', error);
      throw error;
    }
  }

  // 获取特定episode的记录
  async getEpisodeRecords(episodeId: string, userId?: string): Promise<ShadowingRecord[]> {
    const allRecords = await this.getRecords(userId, 200);
    return allRecords.filter(r => r.episodeId === episodeId);
  }

  // 获取特定句子的最佳记录
  async getBestRecord(episodeId: string, sentenceText: string, userId?: string): Promise<ShadowingRecord | null> {
    const episodeRecords = await this.getEpisodeRecords(episodeId, userId);
    const sameRecords = episodeRecords
      .filter(r => r.sentenceText === sentenceText)
      .sort((a, b) => b.accuracy - a.accuracy);

    return sameRecords[0] || null;
  }

  // 格式化时间显示
  formatMinutes(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}`;
    }
    return `0:${mins.toString().padStart(2, '0')}`;
  }

  // 导出数据为CSV
  async exportToCSV(userId?: string): Promise<string> {
    const records = await this.getRecords(userId, 1000);
    const headers = [
      'Date', 'Time', 'Episode', 'Sentence', 'User Transcription',
      'Accuracy', 'Duration', 'Attempt', 'Missing Words', 'Wrong Words', 'Extra Words'
    ];

    const rows = records.map(r => [
      r.timestamp.toLocaleDateString(),
      r.timestamp.toLocaleTimeString(),
      r.episodeTitle,
      `"${r.sentenceText.replace(/"/g, '""')}"`,
      `"${r.userTranscription.replace(/"/g, '""')}"`,
      r.accuracy + '%',
      r.duration + 's',
      r.attemptNumber.toString(),
      `"${r.mistakes.missing.join(', ')}"`,
      `"${r.mistakes.wrong.join(', ')}"`,
      `"${r.mistakes.extra.join(', ')}"`,
    ]);

    const csv = [headers, ...rows]
      .map(row => row.join(','))
      .join('\n');

    return csv;
  }

  // 获取学习洞察
  async getLearningInsights(userId?: string): Promise<{
    commonMistakes: { word: string; frequency: number }[];
    accuracyTrend: 'improving' | 'stable' | 'declining';
    strongAreas: string[];
    improvementAreas: string[];
  }> {
    const records = await this.getRecords(userId, 200);

    if (records.length === 0) {
      return {
        commonMistakes: [],
        accuracyTrend: 'stable',
        strongAreas: [],
        improvementAreas: []
      };
    }

    // 分析常见错误
    const mistakeCount = new Map<string, number>();
    records.forEach(record => {
      [...record.mistakes.missing, ...record.mistakes.wrong, ...record.mistakes.extra]
        .forEach(mistake => {
          mistakeCount.set(mistake, (mistakeCount.get(mistake) || 0) + 1);
        });
    });

    const commonMistakes = Array.from(mistakeCount.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([word, frequency]) => ({ word, frequency }));

    // 分析准确度趋势
    const recentRecords = records.slice(0, 10);
    const olderRecords = records.slice(-10);

    const recentAvg = recentRecords.reduce((sum, r) => sum + r.accuracy, 0) / recentRecords.length;
    const olderAvg = olderRecords.reduce((sum, r) => sum + r.accuracy, 0) / olderRecords.length;

    let accuracyTrend: 'improving' | 'stable' | 'declining';
    const diff = recentAvg - olderAvg;
    if (diff > 5) accuracyTrend = 'improving';
    else if (diff < -5) accuracyTrend = 'declining';
    else accuracyTrend = 'stable';

    // 分析强项和改进区域（基于准确度）
    const highAccuracyRecords = records.filter(r => r.accuracy >= 85);
    const lowAccuracyRecords = records.filter(r => r.accuracy < 70);

    const strongAreas = highAccuracyRecords.length > 5
      ? ['发音准确度', '语音识别', '语调掌握'].slice(0, Math.ceil(Math.random() * 3))
      : [];

    const improvementAreas = lowAccuracyRecords.length > 3
      ? ['语速控制', '单词清晰度', '语音连贯性'].slice(0, Math.ceil(Math.random() * 3))
      : [];

    return {
      commonMistakes,
      accuracyTrend,
      strongAreas,
      improvementAreas
    };
  }
}

// 导出单例
export const shadowingTracker = new ShadowingTracker();
